/**
 * Collection toolbar: filter tabs, sort, grid view, mobile filter drawer.
 */
(function () {
  const STORAGE_KEY = 'airthereal-collection-view';
  const MOBILE_MAX = 749;
  let priceDebounceTimer = null;

  function getFacetForm() {
    return document.getElementById('FacetFiltersForm');
  }

  function dispatchFacetInput(form) {
    if (!form) return;
    form.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Normalizes block settings: param may be "filter.p.m.custom.type" or legacy "filter.p.m.custom.type=Air+Purifier".
   */
  function parseFilterTarget(param, value) {
    let paramName = (param || '').trim();
    let filterValue = (value || '').trim();

    if (paramName.includes('=')) {
      const eqIndex = paramName.indexOf('=');
      const rhs = paramName.slice(eqIndex + 1);
      paramName = paramName.slice(0, eqIndex).trim();
      if (!filterValue && rhs) {
        filterValue = decodeURIComponent(rhs.replace(/\+/g, ' ')).trim();
      }
    }

    return { param: paramName, value: filterValue };
  }

  function valueVariants(filterValue) {
    const decoded = decodeURIComponent((filterValue || '').replace(/\+/g, ' '));
    return [...new Set([filterValue, decoded, decoded.replace(/ /g, '+'), filterValue.replace(/ /g, '+')])].filter(
      Boolean
    );
  }

  function findFacetInput(form, param, value) {
    if (!form) return null;

    const { param: paramName, value: filterValue } = parseFilterTarget(param, value);
    if (!filterValue) return null;

    const variants = valueVariants(filterValue);

    if (paramName) {
      for (const v of variants) {
        const match = form.querySelector(
          `input[type="checkbox"][name="${CSS.escape(paramName)}"][value="${CSS.escape(v)}"]`
        );
        if (match) return match;
      }
    }

    for (const v of variants) {
      const matches = form.querySelectorAll(`input[type="checkbox"][value="${CSS.escape(v)}"]`);
      if (matches.length === 1) return matches[0];
      if (paramName && matches.length > 1) {
        const named = Array.from(matches).find((input) => input.name === paramName);
        if (named) return named;
      }
    }

    const labelNodes = form.querySelectorAll('.facet-checkbox__text-label');
    for (const labelEl of labelNodes) {
      if (labelEl.textContent.trim() !== filterValue && labelEl.textContent.trim() !== decodeURIComponent(filterValue)) {
        continue;
      }
      const input = labelEl.closest('label')?.querySelector('input[type="checkbox"]');
      if (!input) continue;
      if (!paramName || input.name === paramName) return input;
    }

    return null;
  }

  function getParsedFromElement(el) {
    return parseFilterTarget(el.dataset.filterParam, el.dataset.filterValue);
  }

  function isMobileToolbar() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
  }

  function countActiveFilters() {
    const form = getFacetForm();
    if (!form) return 0;
    let count = form.querySelectorAll('input[type="checkbox"]:checked').length;
    form.querySelectorAll('price-range input[type="text"], .facets-price-input').forEach((input) => {
      if (input.value && input.value.trim() !== '') count += 1;
    });
    return count;
  }

  function syncMobileFilterCount() {
    const badge = document.querySelector('[data-collection-filter-count]');
    if (!badge) return;
    const count = countActiveFilters();
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }

  function getDrawer() {
    return document.querySelector('[data-collection-filter-drawer]');
  }

  function syncDrawerFromForm() {
    const form = getFacetForm();
    const drawer = getDrawer();
    if (!form || !drawer) return;

    drawer.querySelectorAll('[data-drawer-pill]').forEach((pill) => {
      const { param, value } = getParsedFromElement(pill);
      let active = false;

      if (!value) {
        if (!form || !param) {
          active = !window.location.search;
        } else {
          active = !form.querySelector(`input[name="${CSS.escape(param)}"]:checked`);
        }
      } else {
        const input = findFacetInput(form, param, value);
        active = Boolean(input?.checked);
      }

      pill.classList.toggle('active', active);
    });

    drawer.querySelectorAll('[data-drawer-checkbox], .swatch-input__input').forEach((drawerInput) => {
      const source = form.querySelector(
        `input[type="checkbox"][name="${CSS.escape(drawerInput.name)}"][value="${CSS.escape(drawerInput.value)}"]`
      );
      if (!source) return;

      drawerInput.checked = source.checked;
      drawerInput.disabled = source.disabled;

      const drawerLabel = drawerInput.closest('.facet-checkbox');
      if (drawerLabel) {
        drawerLabel.classList.toggle('active', source.checked);
        drawerLabel.classList.toggle('disabled', source.disabled);
      }

      const sourceCount = source.closest('.facet-checkbox')?.querySelector('.facet-checkbox__count');
      const drawerCount = drawerLabel?.querySelector('[data-drawer-count]');
      if (sourceCount && drawerCount) {
        drawerCount.textContent = sourceCount.textContent;
      }
    });

    drawer.querySelectorAll('.collection-filter-drawer__price-range input[type="text"]').forEach((input) => {
      const source = form.querySelector(`input[name="${CSS.escape(input.name)}"]`);
      if (source && source.value !== input.value) {
        input.value = source.value;
      }
    });

    syncDrawerSectionCounts();
  }

  function syncDrawerSectionCounts() {
    const form = getFacetForm();
    const drawer = getDrawer();
    if (!form || !drawer) return;

    drawer.querySelectorAll('[data-drawer-section]').forEach((section) => {
      const param = section.dataset.drawerSection;
      const title = section.querySelector('.collection-filter-drawer__section-title');
      if (!title || !param || !section.querySelector('[data-drawer-checkbox]')) return;

      const baseLabel = title.dataset.baseLabel || title.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      const checked = form.querySelectorAll(
        `input[type="checkbox"][name="${CSS.escape(param)}"]:checked`
      ).length;

      if (checked > 0) {
        title.innerHTML = `${baseLabel}<span class="collection-filter-drawer__section-count"> (${checked})</span>`;
      } else {
        title.textContent = baseLabel;
      }
    });
  }

  function syncFilterTabs() {
    const form = getFacetForm();
    document.querySelectorAll('.collection-toolbar__filter-btn').forEach((btn) => {
      const { param, value } = getParsedFromElement(btn);
      let active = false;

      if (!value) {
        if (!form || !param) {
          active = !window.location.search;
        } else {
          active = !form.querySelector(`input[name="${CSS.escape(param)}"]:checked`);
        }
      } else {
        const input = findFacetInput(form, param, value);
        active = Boolean(input?.checked);
      }

      btn.classList.toggle('active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    syncDrawerFromForm();
    syncMobileFilterCount();
  }

  function applyPillToForm(pill, form, submitImmediately) {
    if (!form || pill.disabled) return;

    const { param, value } = getParsedFromElement(pill);

    if (!value) {
      if (param) {
        form.querySelectorAll(`input[name="${CSS.escape(param)}"]:checked`).forEach((input) => {
          input.checked = false;
        });
      } else {
        form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
          input.checked = false;
        });
      }
      if (submitImmediately) dispatchFacetInput(form);
      syncDrawerFromForm();
      return;
    }

    const input = findFacetInput(form, param, value);
    if (!input || input.disabled) return;

    if (pill.dataset.filterSingle === 'true' && param) {
      form.querySelectorAll(`input[name="${CSS.escape(param)}"]`).forEach((el) => {
        el.checked = false;
      });
      input.checked = true;
    } else {
      input.checked = !input.checked;
    }

    if (submitImmediately) dispatchFacetInput(form);
    syncDrawerFromForm();
  }

  function applyCheckboxToForm(drawerInput, form) {
    const source = form.querySelector(
      `input[type="checkbox"][name="${CSS.escape(drawerInput.name)}"][value="${CSS.escape(drawerInput.value)}"]`
    );
    if (!source || source.disabled) {
      drawerInput.checked = false;
      return false;
    }

    source.checked = drawerInput.checked;
    const drawerLabel = drawerInput.closest('.facet-checkbox');
    if (drawerLabel) {
      drawerLabel.classList.toggle('active', drawerInput.checked);
    }
    return true;
  }

  function onDrawerCheckboxChange(event) {
    const drawerInput = event.target;
    if (!drawerInput.matches('[data-drawer-checkbox], .swatch-input__input')) return;

    const form = getFacetForm();
    if (!form) return;

    if (applyCheckboxToForm(drawerInput, form)) {
      dispatchFacetInput(form);
    }
  }

  function onFilterTabClick(event) {
    const btn = event.currentTarget;
    const form = getFacetForm();
    if (!form) return;
    applyPillToForm(btn, form, true);
  }

  function syncDrawerPricesToForm() {
    const form = getFacetForm();
    const drawer = getDrawer();
    if (!form || !drawer) return;

    drawer.querySelectorAll('.collection-filter-drawer__price-range input[type="text"]').forEach((input) => {
      const target = form.querySelector(`input[name="${CSS.escape(input.name)}"]`);
      if (target) target.value = input.value;
    });
  }

  function onDrawerPriceInput() {
    syncDrawerPricesToForm();
    const form = getFacetForm();
    if (!form) return;
    clearTimeout(priceDebounceTimer);
    priceDebounceTimer = setTimeout(() => dispatchFacetInput(form), 800);
  }

  function clearAllFilters() {
    const form = getFacetForm();
    if (!form) return;

    form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      input.checked = false;
    });

    form.querySelectorAll('price-range input[type="text"], .facets-price-input').forEach((input) => {
      input.value = '';
    });

    syncDrawerFromForm();
    syncFilterTabs();
  }

  function getDrawerElements() {
    return {
      drawer: getDrawer(),
      overlay: document.querySelector('[data-collection-filter-overlay]'),
    };
  }

  function openFilterDrawer() {
    const { drawer, overlay } = getDrawerElements();
    if (!drawer || !overlay) return;

    syncDrawerFromForm();

    drawer.hidden = false;
    overlay.hidden = false;
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
    });

    document.body.classList.add('collection-filter-drawer-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
  }

  function closeFilterDrawer() {
    const { drawer, overlay } = getDrawerElements();
    if (!drawer || !overlay || !drawer.classList.contains('is-open')) return;

    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.classList.remove('collection-filter-drawer-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');

    window.setTimeout(() => {
      drawer.hidden = true;
      overlay.hidden = true;
    }, 360);
  }

  function onDrawerApply(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    closeFilterDrawer();
  }

  function onDrawerClear(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const drawer = getDrawer();
    const clearUrl = drawer?.dataset.clearUrl;
    if (window.FacetFiltersForm && clearUrl) {
      const searchParams = clearUrl.indexOf('?') >= 0 ? clearUrl.slice(clearUrl.indexOf('?') + 1) : '';
      window.FacetFiltersForm.renderPage(searchParams);
      syncDrawerFromForm();
      syncFilterTabs();
      return;
    }

    const form = getFacetForm();
    clearAllFilters();
    if (form) dispatchFacetInput(form);
  }

  function onDrawerClick(event) {
    const pill = event.target.closest('[data-drawer-pill]');
    if (!pill) return;

    event.preventDefault();
    const form = getFacetForm();
    if (!form) return;
    applyPillToForm(pill, form, true);
  }

  function bindDrawerEvents() {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.drawerBound === 'true') return;
    drawer.dataset.drawerBound = 'true';

    drawer.addEventListener('change', onDrawerCheckboxChange);
    drawer.addEventListener('click', onDrawerClick);

    drawer.querySelectorAll('.collection-filter-drawer__price-range input[type="text"]').forEach((input) => {
      input.addEventListener('input', onDrawerPriceInput);
    });

    drawer.querySelector('[data-facets-price-go]')?.addEventListener('click', (event) => {
      event.preventDefault();
      syncDrawerPricesToForm();
      const form = getFacetForm();
      if (form) dispatchFacetInput(form);
    });
  }

  function initMobileDrawer() {
    const { drawer, overlay } = getDrawerElements();
    if (!drawer) return;

    bindDrawerEvents();

    if (!document.documentElement.dataset.collectionDrawerClickInit) {
      document.documentElement.dataset.collectionDrawerClickInit = 'true';

      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-collection-filter-open]')) {
          event.preventDefault();
          openFilterDrawer();
          return;
        }

        if (event.target.closest('[data-collection-filter-apply]')) {
          onDrawerApply(event);
          return;
        }

        if (event.target.closest('[data-collection-filter-clear]')) {
          onDrawerClear(event);
          return;
        }

        if (event.target.closest('[data-collection-filter-close]')) {
          event.preventDefault();
          closeFilterDrawer();
          return;
        }

        const { overlay: activeOverlay } = getDrawerElements();
        if (
          event.target.closest('[data-collection-filter-overlay]') &&
          !event.target.closest('[data-collection-filter-drawer]') &&
          activeOverlay?.classList.contains('is-open')
        ) {
          closeFilterDrawer();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) {
          closeFilterDrawer();
        }
      });
    }
  }

  function syncSortFromHidden() {
    const hidden = document.getElementById('SortBy');
    const toolbar = document.querySelector('[data-collection-toolbar-sort]');
    if (hidden && toolbar && toolbar.value !== hidden.value) {
      toolbar.value = hidden.value;
    }
  }

  function onToolbarSortChange(event) {
    const hidden = document.getElementById('SortBy');
    const toolbar = event.currentTarget;
    if (!hidden) return;
    hidden.value = toolbar.value;
    hidden.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function applyView(view, grid) {
    if (!grid) return;
    const cols = view === '4' ? '4' : view === '3' ? '3' : null;

    grid.classList.remove('collection-view--3', 'collection-view--4', 'collection-view--list');
    grid.classList.add(`collection-view--${view}`);

    grid.classList.remove('grid--3-col-desktop', 'grid--4-col-desktop');
    if (cols) {
      grid.classList.add(`grid--${cols}-col-desktop`);
    }

    document.querySelectorAll('.collection-toolbar__view-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.view === view);
    });

    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch (e) {
      /* ignore */
    }
  }

  function getSavedView(grid) {
    const defaultView = grid.dataset.defaultView || '3';
    let saved = defaultView;
    try {
      saved = localStorage.getItem(STORAGE_KEY) || defaultView;
    } catch (e) {
      /* ignore */
    }
    return saved;
  }

  function bindViewToggle() {
    document.querySelectorAll('.collection-toolbar__view-btn').forEach((btn) => {
      if (btn.dataset.viewBound === 'true') return;
      btn.dataset.viewBound = 'true';
      btn.addEventListener('click', () => {
        const grid = document.getElementById('product-grid');
        if (!grid) return;
        applyView(btn.dataset.view, grid);
      });
    });
  }

  function syncViewToggle() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const saved = getSavedView(grid);
    applyView(saved, grid);
    bindViewToggle();
  }

  function initViewToggle() {
    syncViewToggle();
  }

  function syncProductCount() {
    const source = document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
    const target = document.getElementById('ProductCountToolbar');
    if (source && target) {
      target.innerHTML = source.innerHTML;
    }
  }

  function init() {
    document.querySelectorAll('.collection-toolbar__filter-btn').forEach((btn) => {
      btn.addEventListener('click', onFilterTabClick);
    });

    const toolbarSort = document.querySelector('[data-collection-toolbar-sort]');
    if (toolbarSort) {
      toolbarSort.addEventListener('change', onToolbarSortChange);
      syncSortFromHidden();
    }

    initViewToggle();
    initMobileDrawer();
    syncFilterTabs();
    syncProductCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CollectionToolbar = {
    syncFilterTabs,
    syncProductCount,
    syncSortFromHidden,
    syncMobileFilterCount,
    syncDrawerFromForm,
    openFilterDrawer,
    closeFilterDrawer,
    syncViewToggle,
  };
})();
